import React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { AlertTriangle, Copy, Check, EyeOff, Lock } from "lucide-react";
import { cn } from "../../lib/utils";
import type { ApiGatewaySecretRevealDialogProps } from "../../contracts/api-gateway-management";

/**
 * ApiGatewaySecretRevealDialog is a one-time secret reveal surface for newly created keys.
 * It emphasizes that the secret will never be shown again and provides a secure copy flow.
 */
export function ApiGatewaySecretRevealDialog({
  open,
  title = "Key Created Successfully",
  description = "Please copy your new API secret now. For your security, we cannot show it to you again.",
  displayName,
  prefix,
  secret,
  helperText,
  copied,
  copyActionLabel = "Copy Secret",
  acknowledgeActionLabel = "I've saved it",
  onOpenChange,
  onCopy,
  onAcknowledge,
  className,
}: ApiGatewaySecretRevealDialogProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/60 backdrop-blur-sm",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 flex h-fit max-h-[90vh] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden",
            "border border-amber-200 dark:border-amber-900/50 bg-background text-foreground shadow-2xl",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]",
            "data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
            "rounded-2xl",
            className
          )}
        >
          {/* High-Alert Header */}
          <header className="flex flex-col items-center text-center space-y-3 p-8 bg-amber-50/50 dark:bg-amber-950/10 border-b border-amber-100 dark:border-amber-900/30">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
              <AlertTriangle className="h-6 w-6" />
            </div>
            <div className="space-y-1">
              <DialogPrimitive.Title className="text-xl font-bold tracking-tight">
                {title}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="text-sm text-muted-foreground max-w-sm mx-auto leading-relaxed">
                {description}
              </DialogPrimitive.Description>
            </div>
          </header>

          <div className="p-8 space-y-6">
            {/* Key Identity */}
            <div className="space-y-1.5 text-center">
              <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Key Display Name</span>
              <p className="text-sm font-medium">{displayName}</p>
            </div>

            {/* The Secret Surface */}
            <div className="space-y-3">
              <div className="flex items-center justify-between px-1">
                <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Your Secret Key</span>
                <span className="flex items-center gap-1 text-[10px] font-bold text-amber-600 dark:text-amber-400">
                  <EyeOff className="w-3 h-3" />
                  ONE-TIME VIEW
                </span>
              </div>
              
              <div className="relative group">
                <div className="p-4 rounded-xl border-2 border-dashed border-border bg-muted/30 font-mono text-sm break-all pr-12 select-all transition-colors group-hover:border-primary/30">
                  <span className="text-muted-foreground opacity-50">{prefix}</span>
                  <span className="font-bold">{secret}</span>
                </div>
                <button
                  type="button"
                  onClick={onCopy}
                  className={cn(
                    "absolute right-2 top-2 p-2 rounded-lg transition-all",
                    copied 
                      ? "bg-green-500 text-white shadow-lg shadow-green-500/20" 
                      : "bg-background border border-border text-muted-foreground hover:text-primary hover:border-primary/30 shadow-sm"
                  )}
                  title={copyActionLabel ?? undefined}
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Security Notice */}
            <div className="flex gap-3 p-4 rounded-xl bg-muted/50 border border-border">
              <Lock className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-xs font-medium leading-normal">
                  Store this secret in a secure vault like AWS Secrets Manager or 1Password.
                </p>
                {helperText && (
                  <p className="text-[11px] text-muted-foreground leading-normal">
                    {helperText}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <footer className="p-6 border-t border-border bg-muted/10">
            <button
              onClick={onAcknowledge}
              className={cn(
                "w-full py-3 rounded-xl text-sm font-bold transition-all shadow-sm",
                copied
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "bg-muted text-muted-foreground hover:bg-muted/80 cursor-not-allowed opacity-70"
              )}
              disabled={!copied}
            >
              {acknowledgeActionLabel}
            </button>
            {!copied && (
              <p className="text-[10px] text-center mt-3 text-muted-foreground animate-pulse">
                Please copy the secret to enable this button.
              </p>
            )}
          </footer>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
